import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { TRAVAUX_LIST } from "@/lib/travaux-types";
import { ArrowRight, Sparkles, ShieldCheck, HardHat } from "lucide-react";

const seoConfig = {
  title: "Travaux BTP · Rénovation salle de bain, cuisine, extension, isolation · Batisur",
  description: "Sécurisez vos travaux BTP par catégorie : rénovation salle de bain, cuisine, extension maison, isolation thermique, ravalement façade, rénovation appartement, toiture, électricité. Prix 2026, devis gratuits, artisans SIRET vérifiés, paiement escrow Stripe.",
  keywords: [
    "travaux BTP France", "rénovation prix 2026", "devis travaux gratuit",
    "rénovation salle de bain", "rénovation cuisine", "extension maison",
    "isolation thermique ITE", "ravalement façade", "rénovation appartement",
    "toiture couverture", "rénovation électrique",
    "artisan de confiance", "paiement sécurisé travaux",
    "séquestre Stripe BTP", "chantier abandonné recours",
    "MaPrimeRénov 2026", "aide rénovation énergétique",
    "Factur-X 2026 artisan", "Batisur Connect",
  ].join(", "),
  canonical: "/travaux",
  ogType: "website",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Travaux BTP · Toutes catégories",
      "url": `${SITE_URL}/travaux`,
      "description": "Index de toutes les catégories de travaux BTP couvertes par Batisur Connect.",
      "isPartOf": { "@type": "WebSite", "name": SITE_BRAND, "url": SITE_URL },
      "hasPart": TRAVAUX_LIST.map((t) => ({
        "@type": "Service",
        "name": t.label,
        "url": `${SITE_URL}/travaux/${t.slug}`,
        "description": t.seo.description,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Travaux", "item": `${SITE_URL}/travaux` },
      ],
    },
  ],
};

export default function TravauxIndex() {
  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid="travaux-index-page">
      <SEO cfg={seoConfig} />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 relative">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Catégories travaux BTP</div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[0.95]">
            Quel projet <span className="text-[#FF4D00]">de travaux</span> voulez-vous sécuriser ?
          </h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-2xl">
            Choisissez votre catégorie de chantier : chacune bénéficie d'artisans SIRET vérifiés,
            paiement en séquestre Stripe, photos horodatées opposables et médiation professionnelle.
            Fourchettes de prix 2026, devis gratuits en 48h.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#FF4D00]" />
              <span className="mono text-sm">Séquestre Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <HardHat className="w-5 h-5 text-[#FF4D00]" />
              <span className="mono text-sm">Artisans SIRET vérifiés</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FF4D00]" />
              <span className="mono text-sm">Devis gratuits en 48h</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories grid */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TRAVAUX_LIST.map((t) => (
            <Link
              key={t.slug}
              to={`/travaux/${t.slug}`}
              data-testid={`travaux-card-${t.slug}`}
              className="group border-2 border-[#0A0A0B] bg-white p-7 shadow-[4px_4px_0_#0A0A0B] hover:shadow-[6px_8px_0_#FF4D00] hover:-translate-y-1 transition"
            >
              <div className="w-14 h-14 border-2 border-[#0A0A0B] flex items-center justify-center mb-4" style={{ background: t.color }}>
                <HardHat className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{t.hero.overline}</div>
              <h2 className="display text-2xl mt-2 leading-tight group-hover:text-[#FF4D00] transition">
                {t.label}
              </h2>
              <div className="mt-4 mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">
                Fourchette 2026
              </div>
              <div className="display text-lg mt-1">{t.hero.priceRange}</div>
              <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-[#FF4D00]">
                Voir les détails <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA bottom */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// Ne trouve pas votre catégorie ?</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              Publier une mission libre — tous corps de métier acceptés
            </h2>
          </div>
          <Link to="/register?role=demandeur">
            <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2" data-testid="travaux-index-cta-mission">
              Créer ma mission <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
